import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../Assets/RamadreamsGroup.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = useMemo(
    () => [
      { path: "/", label: "Home" },
      { path: "/about", label: "About" },
      { path: "/services", label: "Services" },
      { path: "/properties", label: "Properties" },
      { path: "/contact", label: "Contact" },
    ],
    []
  );

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      y: -16,
      transition: { duration: 0.2, ease: "easeInOut" },
    },
    open: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut", staggerChildren: 0.06 },
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: { duration: 0.2, ease: "easeInOut" },
    },
  };

  const mobileItemVariants = {
    closed: { opacity: 0, x: -10 },
    open: { opacity: 1, x: 0, transition: { duration: 0.2 } },
  };

  return (
    <nav className="bg-secondaryx shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center gap-2">
              <img
                src={logo}
                alt="Ramadreams Group logo"
                className="h-8 w-12"
              />
            </Link>
          </div>

          {/* Menu for large screens */}
          <div className="hidden md:flex items-center space-x-2">
            {navItems.map(({ path, label }) => {
              const isActive = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  className={`${
                    isActive
                      ? "text-primary bg-white"
                      : "text-white hover:text-secondary hover:bg-white/10"
                  } px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300`}
                >
                  {label}
                </Link>
              );
            })}
          </div>

          {/* Hamburger Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-expanded={isOpen}
              aria-label="Toggle navigation menu"
              className="text-white focus:outline-none focus:ring-2 focus:ring-white"
            >
              <svg
                className={`h-6 w-6 transform transition-transform duration-300 ${
                  isOpen ? "rotate-90" : "rotate-0"
                }`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16m-7 6h7"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu with Framer Motion */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden bg-white shadow-lg"
            initial="closed"
            animate="open"
            exit="exit"
            variants={mobileMenuVariants}
          >
            <div className="px-4 py-3 space-y-2">
              {navItems.map(({ path, label }) => {
                const isActive = location.pathname === path;
                return (
                  <motion.div key={path} variants={mobileItemVariants}>
                    <Link
                      to={path}
                      onClick={() => setIsOpen(false)}
                      className={`${
                        isActive
                          ? "text-secondary bg-primaryx"
                          : "text-primary hover:bg-orange-200 hover:text-primaryx"
                      } block w-full text-left px-4 py-3 text-base font-semibold rounded-lg transition-colors`}
                    >
                      {label}
                    </Link>
                  </motion.div>
                );
              })}
              <motion.div variants={mobileItemVariants}>
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center px-4 py-3 text-base font-semibold rounded-lg bg-primaryx text-white hover:bg-secondaryx transition-colors"
                >
                  Get in Touch
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
